import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router';
import { Copy, Check, Lightning } from '@phosphor-icons/react';
import { Highlight, Prism, themes } from 'prism-react-renderer';

/* Register bash/shell — not included in prism-react-renderer defaults */
Prism.languages.bash = {
  comment: { pattern: /(^|[^\\])#.*/, lookbehind: true },
  string: [
    { pattern: /\$'(?:[^'\\]|\\.)*'/, greedy: true },
    { pattern: /"(?:[^"\\$]|\\[\s\S]|\$(?:[^({]|\([^)]*\)|\{[^}]*\}))*"/, greedy: true },
    { pattern: /'[^']*'/, greedy: true },
  ],
  variable: /\$(?:\w+|[!#?*@$]|\{[^}]*\})/,
  keyword: /\b(?:if|then|else|elif|fi|for|while|do|done|case|esac|function|in|select|until|return|exit)\b/,
  builtin: /\b(?:export|echo|cd|pip|npm|npx|node|python|curl|git|docker|claude|sh)\b/,
  flag: /\s--?\w[\w-]*/,
  operator: /&&|\|\||[|<>&]|>>?|\\$/m,
  punctuation: /[;(){}[\]]/,
};
Prism.languages.shell = Prism.languages.bash;

/* ------------------------------------------------------------------ */
/*  Types & Constants                                                   */
/* ------------------------------------------------------------------ */

interface Section {
  id: string;
  label: string;
}

const SECTIONS: Section[] = [
  { id: 'install', label: 'Install' },
  { id: 'quick-start', label: 'Quick Start' },
  { id: 'mcp-http', label: 'MCP Remote (OAuth)' },
  { id: 'mcp-stdio', label: 'MCP Local (stdio)' },
  { id: 'rest-api', label: 'REST API' },
  { id: 'http-proxy', label: 'HTTP Proxy' },
];

/* ------------------------------------------------------------------ */
/*  Sub-components                                                      */
/* ------------------------------------------------------------------ */

type Language = 'bash' | 'typescript' | 'python' | 'json';

/** Infer language from the title string */
function inferLanguage(title: string): Language {
  const t = title.toLowerCase();
  if (t.endsWith('.ts') || t.endsWith('.tsx')) return 'typescript';
  if (t.endsWith('.py')) return 'python';
  if (t.includes('schema') || t.includes('.json')) return 'json';
  return 'bash';
}

function CodeBlock({ title, lang, children }: { title: string; lang?: Language; children: string }) {
  const [copied, setCopied] = useState(false);
  const language = lang ?? inferLanguage(title);

  const handleCopy = () => {
    navigator.clipboard.writeText(children).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-xl border border-[var(--border)] overflow-hidden" style={{ backgroundColor: 'var(--surface-1)' }}>
      {/* Terminal title bar */}
      <div className="flex items-center gap-2 border-b border-[var(--border)] px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <div className="h-2.5 w-2.5 rounded-full bg-[var(--color-error)] opacity-60" />
          <div className="h-2.5 w-2.5 rounded-full bg-[var(--color-warning)] opacity-60" />
          <div className="h-2.5 w-2.5 rounded-full bg-[var(--color-success)] opacity-60" />
        </div>
        <span className="ml-2 text-xs text-text-tertiary font-mono">{title}</span>
        <button
          onClick={handleCopy}
          className="ml-auto flex items-center gap-1.5 rounded-lg border border-[var(--border)] px-2.5 py-1 text-xs font-medium text-text-secondary transition-all hover:border-[var(--text-tertiary)] hover:text-text-primary"
        >
          {copied ? (
            <>
              <Check size={13} weight="bold" className="text-accent-teal" />
              <span className="text-accent-teal">Copied</span>
            </>
          ) : (
            <>
              <Copy size={13} />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <Highlight theme={themes.nightOwl} code={children} language={language}>
        {({ tokens, getLineProps, getTokenProps }) => (
          <pre className="overflow-x-auto p-4 text-sm font-mono leading-relaxed" style={{ background: 'transparent', margin: 0 }}>
            <code>
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </div>
              ))}
            </code>
          </pre>
        )}
      </Highlight>
    </div>
  );
}

function SectionHeader({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="scroll-mt-24 text-2xl font-bold text-text-primary">
      {children}
    </h2>
  );
}

function BestForBadge({ children }: { children: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-teal-muted)] px-3 py-1 text-xs font-medium text-accent-teal">
      <Lightning size={14} weight="fill" />
      Best for: {children}
    </span>
  );
}

function PrerequisitesList({ items }: { items: string[] }) {
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-text-tertiary mb-3">Prerequisites</p>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm text-text-secondary">
            <Check size={16} weight="bold" className="text-accent-teal mt-0.5 shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const INSTALL_METHODS = [
  { id: 'curl', label: 'curl', command: 'curl -fsSL https://app.palaryn.com/install.sh | sh' },
  { id: 'brew', label: 'brew', command: 'brew install palaryn-ai/tap/palaryn' },
] as const;

function InstallBlock() {
  const [method, setMethod] = useState<string>(INSTALL_METHODS[0].id);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const current = INSTALL_METHODS.find((m) => m.id === method)!;

  const handleCopy = () => {
    navigator.clipboard.writeText(current.command).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="relative">
      <div className="rounded-xl border border-[var(--border)] flex items-center gap-0 px-4 py-3" style={{ backgroundColor: 'var(--surface-1)' }}>
        {/* Method selector */}
        <div className="relative">
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-1.5 rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm font-medium text-text-primary hover:border-[var(--text-tertiary)] transition-colors"
          >
            {current.label}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-text-tertiary">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {open && (
            <div className="absolute top-full left-0 mt-1 z-50 rounded-lg border border-[var(--border)] bg-[var(--surface-1)] py-1 shadow-lg min-w-[120px]">
              {INSTALL_METHODS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => { setMethod(m.id); setOpen(false); }}
                  className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-colors ${
                    m.id === method ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-[var(--surface-0)]'
                  }`}
                >
                  {m.id === method && <Check size={12} weight="bold" className="text-accent-teal" />}
                  {m.id !== method && <span className="w-3" />}
                  {m.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Command */}
        <code className="ml-3 flex-1 text-sm font-mono text-text-secondary truncate">{current.command}</code>

        {/* Copy button */}
        <button
          onClick={handleCopy}
          className="ml-3 shrink-0 rounded-lg border border-[var(--border)] p-2 text-text-secondary hover:border-[var(--text-tertiary)] hover:text-text-primary transition-all"
        >
          {copied ? <Check size={14} weight="bold" className="text-accent-teal" /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Docs Component                                                 */
/* ------------------------------------------------------------------ */

export function Docs() {
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Scroll to top on mount (React Router preserves scroll position)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        setActiveSection(entry.target.id);
        break;
      }
    }
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(handleIntersection, {
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0.1,
    });

    for (const section of SECTIONS) {
      const el = document.getElementById(section.id);
      if (el) observerRef.current.observe(el);
    }

    return () => observerRef.current?.disconnect();
  }, [handleIntersection]);

  return (
    <div className="min-h-screen bg-surface-0">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[var(--border)] backdrop-blur-xl" style={{ backgroundColor: 'rgba(11, 14, 19, 0.8)' }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2.5 text-text-primary hover:text-text-secondary transition-colors">
            <img src="/logo.png" alt="Palaryn" width="22" height="22" />
            <span className="text-sm font-medium">Palaryn</span>
          </Link>
          <Link
            to="/login"
            className="text-sm text-text-tertiary hover:text-text-secondary transition-colors"
          >
            Sign in
          </Link>
        </div>
      </header>

      {/* Mobile pill nav */}
      <div className="lg:hidden sticky top-[57px] z-40 border-b border-[var(--border)] bg-surface-0 overflow-x-auto">
        <div className="flex gap-2 px-4 py-2.5">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                activeSection === s.id
                  ? 'bg-[var(--color-teal-muted)] text-accent-teal'
                  : 'text-text-tertiary hover:text-text-secondary'
              }`}
            >
              {s.label}
            </a>
          ))}
        </div>
      </div>

      {/* Two-column layout */}
      <div className="mx-auto max-w-6xl px-6 py-10 lg:grid lg:grid-cols-[220px_1fr] lg:gap-10">
        {/* Sidebar TOC (desktop) */}
        <aside className="hidden lg:block">
          <nav className="sticky top-24 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-text-tertiary mb-3">Integrations</p>
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={`block rounded-lg px-3 py-2 text-sm transition-colors ${
                  activeSection === s.id
                    ? 'bg-[var(--color-teal-muted)] text-accent-teal font-medium'
                    : 'text-text-secondary hover:text-text-primary hover:bg-[var(--surface-1)]'
                }`}
              >
                {s.label}
              </a>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="min-w-0 space-y-20">
          {/* Page title */}
          <div>
            <h1 className="text-4xl font-bold text-text-primary">Integration Docs</h1>
            <p className="mt-3 text-text-secondary leading-relaxed">
              Palaryn supports multiple integration methods. Pick the one that fits your stack — every request flows through the same 10-step security pipeline.
            </p>
          </div>

          {/* ---- Install ---- */}
          <section className="space-y-6">
            <SectionHeader id="install">Install</SectionHeader>
            <InstallBlock />

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">CLI Commands</h3>
              <CodeBlock title="terminal — usage">{`# Start the gateway server (default command)
palaryn start --port 3000 --no-auth

# Start MCP stdio server (for AI tool integrations)
palaryn mcp --workspace ws_abc --actor my-agent

# Show help
palaryn --help`}</CodeBlock>
            </div>
          </section>

          {/* ---- Quick Start ---- */}
          <section className="space-y-6">
            <SectionHeader id="quick-start">Quick Start</SectionHeader>
            <BestForBadge>trying Palaryn in under 60 seconds</BestForBadge>
            <PrerequisitesList items={[
              'A Palaryn workspace and API key (grab one from Settings → API Keys)',
              'curl or any HTTP client',
            ]} />
            <p className="text-sm text-text-secondary leading-relaxed">
              The fastest way to test the gateway is a single curl call. This hits the REST API directly and runs your request through the full pipeline (auth, rate limiting, policy, DLP, budget, execution, audit).
            </p>
            <CodeBlock title="terminal — quick start">{`curl -X POST https://app.palaryn.com/v1/tool/execute \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: pk_live_your_api_key" \\
  -d '{
    "tool": "http.get",
    "input": {
      "url": "https://api.github.com/repos/octocat/Hello-World"
    },
    "taskId": "demo-task-1",
    "actorId": "my-agent"
  }'`}</CodeBlock>
          </section>

          {/* ---- MCP HTTP (OAuth) ---- */}
          <section className="space-y-6">
            <SectionHeader id="mcp-http">MCP Remote — HTTP Transport (OAuth)</SectionHeader>
            <BestForBadge>Claude Code, Cursor, and any MCP client (recommended)</BestForBadge>
            <PrerequisitesList items={[
              'A Palaryn account (sign up at app.palaryn.com)',
              'An MCP-compatible client (Claude Code, Cursor, Windsurf, etc.)',
            ]} />
            <p className="text-sm text-text-secondary leading-relaxed">
              The fastest way to connect your AI tools to Palaryn. One command, OAuth login in the browser, and you're done — no local setup, no environment variables. Your MCP client connects to the hosted Palaryn instance over HTTPS with automatic token management.
            </p>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Setup (one command)</h3>
              <CodeBlock title="terminal">{`# Claude Code
claude mcp add --transport http palaryn https://app.palaryn.com/mcp

# Cursor — add to .cursor/mcp.json
{
  "mcpServers": {
    "palaryn": {
      "url": "https://app.palaryn.com/mcp"
    }
  }
}`}</CodeBlock>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">OAuth 2.0 Flow</h3>
              <p className="text-sm text-text-secondary mb-3">
                Authentication is fully automatic. On first use:
              </p>
              <div className="space-y-3 text-sm text-text-secondary">
                <div className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-teal-muted)] text-xs font-bold text-accent-teal">1</span>
                  <span><strong className="text-text-primary">Dynamic client registration</strong> — your MCP client registers itself automatically with the Palaryn OAuth provider</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-teal-muted)] text-xs font-bold text-accent-teal">2</span>
                  <span><strong className="text-text-primary">Authorization redirect</strong> — browser opens to the Palaryn login page</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-teal-muted)] text-xs font-bold text-accent-teal">3</span>
                  <span><strong className="text-text-primary">Login &amp; consent</strong> — sign in with your Palaryn credentials and approve the connection</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-teal-muted)] text-xs font-bold text-accent-teal">4</span>
                  <span><strong className="text-text-primary">Token exchange</strong> — the MCP client receives an access token and connects to the gateway</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-text-secondary leading-relaxed">
              After initial setup, the token refreshes automatically. All tool calls flow through the same pipeline with your workspace policies, DLP rules, and budget limits applied.
            </p>
          </section>

          {/* ---- MCP Bridge (stdio) ---- */}
          <section className="space-y-6">
            <SectionHeader id="mcp-stdio">MCP Local — stdio Bridge</SectionHeader>
            <BestForBadge>self-hosted or air-gapped environments</BestForBadge>
            <PrerequisitesList items={[
              'Palaryn CLI installed (npm install -g palaryn)',
              'An MCP-compatible client (Claude Code, Cursor, etc.)',
            ]} />
            <p className="text-sm text-text-secondary leading-relaxed">
              For self-hosted deployments or environments without internet access. The MCP stdio bridge runs as a local subprocess — your MCP client spawns the <code className="rounded bg-[var(--surface-1)] px-1.5 py-0.5 text-xs font-mono text-text-primary">palaryn mcp</code> command, communicates over stdin/stdout, and every tool call routes through the gateway pipeline.
            </p>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Setup (Claude Code)</h3>
              <CodeBlock title="terminal">{`# Add the local Palaryn MCP server
claude mcp add palaryn -- palaryn mcp`}</CodeBlock>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Environment Variables</h3>
              <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border)]">
                      <th className="px-4 py-2.5 text-left font-medium text-text-primary">Variable</th>
                      <th className="px-4 py-2.5 text-left font-medium text-text-primary">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-subtle)]">
                    {[
                      ['PALARYN_MCP_WORKSPACE', 'Workspace ID for tool call routing'],
                      ['PALARYN_MCP_ACTOR', 'Actor ID (e.g., "claude-code")'],
                      ['PALARYN_MCP_PLATFORM', 'Platform identifier (e.g., "claude")'],
                    ].map(([variable, desc]) => (
                      <tr key={variable}>
                        <td className="px-4 py-2.5 font-mono text-xs text-accent-teal">{variable}</td>
                        <td className="px-4 py-2.5 text-text-secondary">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Available Tools</h3>
              <p className="text-sm text-text-secondary">
                Once connected, your MCP client sees Palaryn tools like <code className="rounded bg-[var(--surface-1)] px-1.5 py-0.5 text-xs font-mono text-text-primary">http_get</code>,{' '}
                <code className="rounded bg-[var(--surface-1)] px-1.5 py-0.5 text-xs font-mono text-text-primary">http_post</code>,{' '}
                <code className="rounded bg-[var(--surface-1)] px-1.5 py-0.5 text-xs font-mono text-text-primary">http_request</code> — all routed through the gateway pipeline.
              </p>
            </div>
          </section>

          {/* ---- REST API ---- */}
          <section className="space-y-6">
            <SectionHeader id="rest-api">REST API</SectionHeader>
            <BestForBadge>direct HTTP integration from any language</BestForBadge>
            <PrerequisitesList items={[
              'A Palaryn API key',
              'Any HTTP client (fetch, axios, requests, etc.)',
            ]} />
            <p className="text-sm text-text-secondary leading-relaxed">
              The core endpoint is <code className="rounded bg-[var(--surface-1)] px-1.5 py-0.5 text-xs font-mono text-text-primary">POST /v1/tool/execute</code>. Every agent tool call goes through this single endpoint.
            </p>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Request Body</h3>
              <CodeBlock title="request schema">{`{
  "tool": "http.get" | "http.post" | "http.put" | "http.delete" | "slack.post" | ...,
  "input": {
    "url": "https://...",
    "method": "GET",           // optional, inferred from tool name
    "headers": {},             // optional
    "body": {}                 // optional, for POST/PUT/PATCH
  },
  "taskId": "unique-task-id",  // groups calls for budget tracking
  "actorId": "agent-name",     // identifies the calling agent
  "platform": "claude"         // optional: claude | openai | langchain | custom
}`}</CodeBlock>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Response Shape</h3>
              <CodeBlock title="response schema">{`{
  "tool_call_id": "...",
  "task_id": "...",
  "status": "ok" | "blocked" | "pending_approval",
  "policy": {
    "decision": "allow",
    "rule_id": "Allow read operations",
    "reasons": ["Matched rule ..."]
  },
  "dlp": {
    "detected": [],            // secrets/PII detected
    "redactions": [],          // applied redactions
    "severity": "low"          // low | medium | high | critical
  },
  "budget": {
    "estimated_cost_usd": 0.001,
    "spent_cost_usd_task": 0.001,
    "remaining_cost_usd_task": 1.999
  },
  "output": {                  // the actual HTTP response (when allowed)
    "http_status": 200,
    "body": { ... },
    "headers": { ... }
  },
  "timing": {
    "started_at": "2026-...",
    "duration_ms": 142
  }
}`}</CodeBlock>
            </div>
          </section>

          {/* ---- HTTP Proxy ---- */}
          <section className="space-y-6">
            <SectionHeader id="http-proxy">HTTP Forward Proxy</SectionHeader>
            <BestForBadge>zero-code integration with any HTTP client</BestForBadge>
            <PrerequisitesList items={[
              'Palaryn gateway running with PROXY_ENABLED=true',
              'A workspace ID and API key',
            ]} />
            <p className="text-sm text-text-secondary leading-relaxed">
              The forward proxy intercepts outgoing HTTP requests at the network layer — no SDK or code changes needed. Set a single environment variable and all HTTP traffic from your agent flows through the Palaryn pipeline.
            </p>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Setup</h3>
              <CodeBlock title="terminal — environment variable">{`# Format: http://WORKSPACE_ID:API_KEY@gateway-host:3128
export HTTP_PROXY=http://ws_abc123:pk_live_your_key@gateway:3128
export HTTPS_PROXY=http://ws_abc123:pk_live_your_key@gateway:3128`}</CodeBlock>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">How It Works</h3>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start gap-2">
                  <span className="text-accent-teal font-mono text-xs mt-0.5">HTTP</span>
                  <span>Layer 7 interception — full request/response inspection, DLP scanning, and policy enforcement</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-accent-teal font-mono text-xs mt-0.5">HTTPS</span>
                  <span>Layer 4 CONNECT tunnel — domain-level policy enforcement and SSRF protection without TLS termination</span>
                </li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">Example</h3>
              <CodeBlock title="terminal — transparent proxying">{`# With HTTP_PROXY set, curl goes through Palaryn automatically
curl https://api.github.com/repos/octocat/Hello-World

# Python requests also respects HTTP_PROXY
python -c "import requests; print(requests.get('https://api.github.com').status_code)"

# Passthrough domains bypass the pipeline (configured in policy pack)
# e.g., internal services, health checks`}</CodeBlock>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
